/**
 * Copyright (c) S2 Partnership Ltd. 2022
 *
 * Defines interfaces to represent data models for data that comes down in the API,
 * typically for the login request and during branding translation requests.
 *
 * @summary Defines API interface data models.
 * @author S2 Partnership Ltd
 *
 * Created at     : 17-06-2021
 */

export class LoginData {
  allowAuditMng: number;
  /** The ACL for being able to create scheduled inspections for the current user. */
  allowInspectionDueForReview: number;
  /** Stores the action categories for the current client. */
  categories: Category[];
  /** Stores the company logo being sent down for the current client. */
  companyLogo: CompanyLogo;
  /** The ACL for being able to complete business outstanding actions using the other option. */
  completeOtherBusinessActions: number;
  completeOtherEnviromentalActions: number;
  /** The ACL for being able to complete environmental outstanding actions using the other option. */
  completeOtherEnvironmentalActions: number;
  /** The ACL for being able to complete health outstanding actions using the other option. */
  completeOtherHealthActions: number;
  /** The ACL for being able to complete incident outstanding actions using the other option. */
  completeOtherIncidentActions: number;
  /** The ACL for being able to complete property management outstanding actions using the other option. */
  completeOtherPropertyManagementActions?: number;
  /** The ACL for being able to complete business outstanding actions using the prescribed option. */
  completePrescribedBusinessActions: number;
  completePrescribedEnviromentalActions: number;
  /** The ACL for being able to complete environmental outstanding actions using the prescribed option. */
  completePrescribedEnvironmentalActions: number;
  /** The ACL for being able to complete health outstanding actions using the prescribed option. */
  completePrescribedHealthActions: number;
  /** The ACL for being able to complete incident outstanding actions using the prescribed option. */
  completePrescribedIncidentActions: number;
  /** The ACL for being able to complete property management outstanding actions using the prescribed option. */
  completePrescribedPropertyManagementlActions?: number;
  /** The ACL for being able to email details about business outstanding actions. */
  emailBusinessActionDetails: number;
  emailEnviromentalActionDetails: number;
  /** The ACL for being able to email details about environmental outstanding actions. */
  emailEnvironmentalActionDetails: number;
  /** The ACL for being able to email details about health outstanding actions. */
  emailHealthActionDetails: number;
  /** The ACL for being able to email details about incident outstanding actions */
  emailIncidentActionDetails: number;
  /** The ACL for being able to email details about property management outstanding actions. */
  emailPropertyManagementActionDetails?: number;
  /** Defines whether the current user logging in has accepted the Eula or not. */
  eulaAccepted: number;
  /** Stored the Eula message for the current client. */
  eulaMessage?: string;
  /** Stores a list of properties available to this user. */
  properties: LoginProperty[];
  /** Stores the severities for the current client. */
  severities: Severity[];
  /** The timeout is milliseconds for auto logging out due to inactivity. */
  timeout: number;
  /** The ACL for being able to add an update progress on any outstanding action on all module. */
  updateProgressAllActions: number;
  /** Stores data about the user logging in. */
  userInformation: UserInformation;
  /** The ACL for being able to view and download business outstanding actions. */
  viewDownloadBusinessActions: number;
  viewDownloadEnviromentalActions: number;
  /** The ACL for being able to view and download environmental outstanding actions. */
  viewDownloadEnvironmentalActions: number;
  /** The ACL for being able to view and download health outstanding actions. */
  viewDownloadHealthActions: number;
  /** The ACL for being able to view and download incident outstanding actions. */
  viewDownloadIncidentActions: number;
  /** The ACL for being able to view and download property management outstanding actions. */
  viewDownloadPropertyManagementActions?: number;
  /** The ACL for restricting the option of continuing a scheduled event on it's current schedule. */
  restrictContinueEventSchedule?: number;
  /** The ACL for restricting the option of ending a scheduled event. */
  restrictEndEventSchedule?: number;
  /** The ACL for restricting the option to reschedule a scheduled event to a new date. */
  restrictRescheduleEventSchedule?: number;
  /** The control panel switch to allow geo-location to be recorded against an inspection. */
  allowGeoLocation?: number;
  /** The ACL for being able to add health actions. */
  addHealthActions?: number;
  /** The ACL for being able to add environmental actions. */
  addEnvironmentalActions?: number;
  /** The ACL for being able to add business actions. */
  addBusinessActions?: number;
  /** The ACL for being able to add property management actions. */
  addPropertyManagementActions?: number;
  /** The ACL for being  able to add incident actions. */
  addIncidentActions?: number;
  /** The maximum number of properties that can be synced down at once. */
  syncLimit?: number;

  /** ACLs for adding new events */
  healthAddNew?: number;
  environmentAddNew?: number;
  contractorAddNew?: number;
  propertyManagementAddNew?: number;
  continuityAddNew?: number;
  accidentAddNew?: number;

  /** ACLs for adding reviews for events */
  healthAddReview?: number;
  environmentAddReview?: number;
  contractorAddReview?: number;
  propertyManagementAddReview?: number;
  continuityAddReview?: number;

  /**
   * Standard Angular Constructor
   *
   * @ignore
   */
  constructor(
    /* eslint-disable */
    rawData: any
    /* eslint-enable */
  ) {
    for (const key of Object.keys(rawData)) {
      this[key] = rawData[key];
    }
  }
}
export interface Category {
  id: number;
  moduleCategory: string;
  moduleId: number;
}
export interface CompanyLogo {
  downloadLink: string;
  extension: string;
  fileContent: any;
  id: number;
  name: string;
  size: number;
  type: string;
}
export interface LoginProperty {
  id: number;
  name: string;
}
export interface Severity {
  actionModuleId: number;
  backColor: string;
  id: number;
  moduleId: number;
  moduleName: string;
  name: string;
  numDays: number;
  textColor: string;
  weight: number;
}
export interface UserInformation {
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  country: number;
  county: number;
  email: string;
  fName: string;
  fax: number;
  jobTitle: string;
  lName: string;
  organisationName: string;
  phone: number;
  phone2: number;
  postcode: string;
  town: string;
}

/**
 * Class to define App Branding objects. Colours and the logo.
 */
export class AppBranding {
  brandingColours: BrandingColours;
  logo: BrandingLogo;

  /**
   * Standard Angular Constructor
   *
   * @ignore
   */
  constructor(
    /* eslint-disable */
    rawData: any
    /* eslint-enable */
  ) {
    for (const key of Object.keys(rawData)) {
      this[key] = rawData[key];
    }
  }
}

export interface BrandingColours {
  primary: string;
  secondary: string;
  tertiary: string;
  success: string;
  warning: string;
  danger: string;
  dark: string;
  medium: string;
  light: string;
}

export interface BrandingLogo {
  extension: string;
  fileContent: string;
  name: string;
  url: string;
}

/**
 * An interface object for storing the language that was selected in the settings.
 */
export interface LanguageSelected {
  code: string;
}
